import { isString, get as getKey } from 'lodash';
import React, {
  useEffect,
} from 'react';
import confetti from 'canvas-confetti';
import styled from 'styled-components';

import { stringify as stringifyQuery } from 'querystring';

import {
  UploadOutlined,
  CheckOutlined,
} from '@ant-design/icons';

// import AntdDivider from 'antd/lib/divider';

import Admin from 'hive-admin';

import {
  asLocalStorageField,
} from 'hive-admin/src/components/FieldLocalStorage';

import useQuery from '../../modules/useQuery';

import FontPreview from '../../components/FontPreview';

import Types from '../../modules/types';

import {
  fieldStyleTheme as fieldStyleMode,
  fieldStyleRoundness,
  fieldStyleTransitionStyle,

  fieldAccentColorPrimary,
  fieldAccentColorSecondary,
  fieldLanguageRaw,
  fieldLanguagesRaw,
} from '../stores/fields';

import {
  fieldSlug as fieldCategorySlug,
  fieldSubcategories as fieldCategorySubcategories,
} from '../categories/fields';

import {
  fieldI18nLanguage,
  createGetI18nFields,
} from '../../helpers/i18n';
import copyToClipboard, { getServerUrl } from '../../helpers/copyToClipboard';
import getFilterWithCol from '../../helpers/getFilterWithCol';
import cloneItem from '../../helpers/cloneItem';
import createSlug from '../../helpers/createSlug';

// import domainInstructionsSrc from '../../assets/onboarding-domain.pdf';
import termsAndConditionsSrc from '../../assets/onboarding-terms.pdf';

import { tests } from './helpers';

import { FIELDS as fieldsHomepage } from './fieldsHomepage';

const {
  REACT_APP_DXTOOLS_ENV,
} = process.env;

export const fieldCurrentStep = ['FieldSelect', {
  name: 'onboarding.currentStep',
  label: 'Current Step:',
  choices: Types.ONBOARDING_STEPS_LIST.map(({ id, label }) => ({
    value: id,
    label,
  })),
  skip: [() => REACT_APP_DXTOOLS_ENV !== 'development'],
  // disabled: () => true,
}];

export const fieldStoreName = ['FieldText', {
  name: 'name',
  label: 'Store Name',
  skip: [tests.skipIfStepNotStore],
}];

export const fieldStoreCategories = ['FieldSelect', {
  name: 'storeCategories',
  label: 'Industries',
  placeholder: 'Choose the industry that best describes your store.',
  skip: [tests.skipIfStepNotStore],
  rules: [['validation.isRequired']],
  mode: 'multiple',
  choices: Types.STORE_CATEGORIES_LIST.map(({ id: value, label }) => ({
    value,
    label,
  })),
}];

export const fieldStoreSlug = ['FieldText', {
  name: 'slug',
  label: 'Link',
  getInitialValue: (_, props) => props?.data?.slug,
  skip: [tests.skipIfStepNotStore],
  addonBefore: `${process.env.REACT_APP_SERVER_HOST}/`,
  help: `
    Link for your store on our platform,
    from which your store can be accessed.
  `,
}];

// BRAND

export const fieldBrandColor = ['FieldColorPicker', {
  name: 'brandColor',
  label: 'Brand Color',
  rules: [['validation.isRequired'], [(config = {}) => ({
    validator: (rule, value, cb) => (cb(
      (!value || !value.length)
        ? undefined
        : /^(hsla?\(|#)/.test(value)
        ? undefined
        : rule.message
    )),
    // eslint-disable-next-line max-len
    message: 'Color must be either hsl (eg. hsl(327, 46%, 46%)) or a valid HEX String (eg. #AA407A)',
    ...config,
  })]],
  getInitialValue: (_, props) => props?.data?.brandColor,
  skip: [tests.skipIfStepNotBrand],
}];

export const fieldBrandAccentColorPrimary = cloneItem(
  fieldAccentColorPrimary,
  12,
  null,
  {
    skip: [tests.skipIfStepNotBrand],
  }
);

export const fieldBrandAccentColorSecondary = cloneItem(
  fieldAccentColorSecondary,
  12,
  null,
  {
    skip: [tests.skipIfStepNotBrand],
  }
);

export const fieldBrandLogo = ['FieldUploadRefs', {
  name: 'logo',
  label: 'Logo',
  accept: 'image/jpg,image/jpeg,image/png,image/webp',
  transformations: [
    ['GM', { command: 'compress', maxWidth: 700 }],
    ['GM', { command: 'webp' }],
  ],
  col: 1,
  getInitialValue: (_, props) => props?.data?.logo,
  skip: [
    tests.skipIfStepNotBrand,
    [
      'condition.testFieldValue',
      'onboarding.brandLogo',
      '===',
      false,
    ],
  ],
}];

export const fieldBrandFavicon = ['FieldUploadRefs', {
  name: 'favicon',
  label: 'Favicon',
  accept: 'image/png,image/*',
  transformations: [
    ['GM', { command: 'compress', maxWidth: 300 }],
  ],
  col: 1,
  getInitialValue: (_, props) => props?.data?.favicon,
  skip: [
    tests.skipIfStepNotBrand,
    [
      'condition.testFieldValue',
      'onboarding.brandLogo',
      '===',
      false,
    ],
  ],
}];

export const fieldAiBioShort = ['FieldTextArea', {
  label: 'Short Bio',
  name: 'ai.bioShort',
  placeholder: 'Explain your store motto in a sentence',
  autoSize: { minRows: 1 },
  rules: [['validation.isRequired']],
  skip: [
    tests.skipIfStepNotBrand,
  ],
}];

const fieldsSocial = [
  ['facebook', 'Facebook'],
  ['instagram', 'Instagram'],
  ['twitter', 'Twitter'],
  ['whatsapp', 'WhatsApp'],
  ['tiktok', 'TikTok'],
  ['snapchat', 'Snapchat'],
  ['youtube', 'YouTube'],
].map((([name, label]) => (['FieldText', {
  name: `${name}Href`,
  label: (
    <div>
      {label}
    </div>
  ),
  skip: [tests.skipIfStepNotStore],
  hidden: [[
    'condition.fieldIsNotTrue',
    'show_fields_store_social',
  ]],
}])));

// CATEGORIES

export const fieldCategories = ['FieldSortableList', {
  name: 'categories',
  // condensed: true,
  getItemFields: createGetI18nFields({ languagesDataKey: 'languages' }),
  fields: [
    ['FieldText', {
      name: 'name',
      label: null,
      rules: [['validation.isRequired']],
      i18nTranslatable: true,
      prepareValueForForm: (value, props) => {
        let i18nLanguageValue = null;
        const form = (
          props.forms
            ? props.forms[props.forms.length - 1]
            : props.form
        );
        if (form && form.getFieldValue) {
          i18nLanguageValue = form.getFieldValue('i18nLanguage') || 'en';
        } else {
          i18nLanguageValue = 'en';
        }
        if (i18nLanguageValue === 'en') {
          props.form.setFieldsValue({
            slug: createSlug(value?.target?.value),
          });
        }
        return value;
      },
    }],
    cloneItem(fieldCategorySlug, null, null, {
      label: null,
    }),
    cloneItem(fieldCategorySubcategories, null, null, {
      label: null,
      getItemFields: createGetI18nFields({ languagesDataKey: 'languages' }),
    }),
  ],
  skip: [tests.skipIfStepNotCategories],
}];

// PRODUCTS

export const fieldsProducts = [
  ['FieldHidden', {
    name: 'productsImportRefresh',
    initialValue: 0,
  }],
  ['FieldReact', {
    name: 'productsImport',
    renderContent: ({
      LoadProductsImportComponent,
      ...props
    }) => (
      <LoadProductsImportComponent {...props} />
    ),
    LoadProductsImportComponent: (props) => {
      // eslint-disable-next-line no-unused-vars
      const [{ loading, data }, reload] = useQuery(props.client, {
        // skip: !props.value?._id,
        url: `products-imports?${stringifyQuery({
          query: JSON.stringify({
            includeGalleryInfo: true,
            refresh: props.form.getFieldValue('productsImportRefresh'),
            where: {
              // _id: props.value?._id,
              store: props.form.getFieldValue('store'),
            },
            limit: 1,
          }),
        })}`,
      });
      const { onChange } = props;
      useEffect(
        () => {
          if (data?.data?.data?.[0]) {
            onChange(data.data.data[0]);
          }
        },
        [data, onChange],
      );
      // console.log(loading, data);
      return null;
    },
  }],
  ['FieldConnectionSelect', {
    name: 'store',
    label: null,
    url: '/stores',
    placeholder: 'Choose the Store to upload products for',
    allowClear: true,
    searchPaths: ['name'],
    hidden: [() => true],
    getInitialValue: (_, props) => {
      if (props.viewer?.storeData?._id) {
        return props.viewer.storeData._id;
      }
      return null;
    },
  }],
  ['FieldProductsImportCsv', {
    redirectOnCreate: false,
    getIcon: (props) => {
      if (props.form.getFieldValue('productsImport')) {
        return <CheckOutlined />
      }
      return <UploadOutlined />;
    },
    getPlaceholder: (props) => {
      if (props.form.getFieldValue('productsImport')) {
        return 'Done';
      }
      return 'Products CSV';
    },
    style: { height: '120px' },
    disabled: [
      props => props.form.getFieldValue('productsImport'),
    ],
    onCreate: (data, props) => {
      props.form.setFieldsValue({
        productsImportRefresh: (
          props.form.getFieldValue('productsImportRefresh') + 1
        ),
      });
    },
  }],
  ['FieldUploadRefsDropzone', {
    name: 'gallery',
    accept: 'image/jpg,image/jpeg,image/png,image/webp',
    getIcon: (props) => {
      const productsImport = props.form.getFieldValue('productsImport');
      if (productsImport?.galleryCount > 0) {
        return <CheckOutlined />
      }
      return <UploadOutlined />;
    },
    getPlaceholder: (props) => {
      const productsImport = props.form.getFieldValue('productsImport');
      if (productsImport?.galleryCount > 0) {
        return 'Done';
      }
      return 'Gallery Folder';
    },
    disabled: [
      props => {
        const productsImport = props.form.getFieldValue('productsImport');
        if (!productsImport || productsImport.galleryCount > 0) {
          return true;
        }
        return false;
      },
    ],
    transformations: [
      ['GM', { command: 'compress', maxWidth: 1500 }],
      ['GM', { command: 'webp' }],
    ],
    style: { height: '120px' },
    virtual: [() => true],
    getUploadArgs: (args, props) => {
      args.tag = props.form.getFieldValue('productsImport')?.galleryTag;
      return args;
    },
    // eslint-disable-next-line no-unused-vars
    onUploadSuccess: async (uploads, props) => {
      Admin.showMessage(
        'Images uploaded successfully!',
        'success',
        3,
      );
      props.form.setFieldsValue({
        productsImportRefresh: true,
      });
    },
  }],
].map(([fieldName, fieldConfig = {}]) => ([fieldName, {
  ...fieldConfig,
  skip: [
    ...(fieldConfig.skip || []),
    tests.skipIfStepNotProducts,
  ],
}]));

// CONTENT

const fieldsContentI18nGetFieldName = (field, languageId) => {
  if (languageId === 'en') {
    return field.name;
  }
  const fieldName = field.name.split('.').reduce(
    (agrNameParts, part, i, parts) => {
      if (i + 1 === parts.length) {
        agrNameParts.push('i18n', languageId);
      }
      agrNameParts.push(part);
      return agrNameParts;
    },
    [],
  ).join('.');
  return fieldName;
};

const PageContentFieldsImage = styled.video`
  width: 100%;
  min-width: 300px;
  aspect-ratio: 1/1;
  object-fit: cover;
`;

export const fieldsContent = Types.PAGE_CONTENT_FIELDS_SECTIONS_ITEMS.reduce(
  (agr, section) => {
    const skip = [tests.skipIfStepNotContent];
    agr.push(['FieldTitle', {
      title: section.label,
      skip,
    }]);
    section.fieldIds.forEach((fieldId) => {
      const field = Types.PAGE_CONTENT_FIELDS_MAP[fieldId];
      if (field.type === 'IMAGE') {
        agr.push(['FieldHidden', {
          name: `pageContentFields.${field.id}ChoicesGenerated`,
          skip,
        }]);
        agr.push(['FieldHidden', {
          name: `pageContentFields.${field.id}ChoicesProducts`,
          skip,
        }]);
        agr.push(['FieldRadio', {
          label: field.label,
          name: `pageContentFields.${field.id}Type`,
          choices: [{
            value: 'STOCK',
            label: 'Stock',
          // }, {
          //   value: 'PRODUCTS',
          //   label: 'Products',
          }, {
            value: 'UPLOAD',
            label: 'Upload',
          }],
          block: true,
          skip,
        }]);
        agr.push(['FieldRadioAdvanced', {
          name: `pageContentFields.${field.id}`,
          label: null,
          skip: [
            ...skip,
            [
              'condition.testFieldValue',
              `pageContentFields.${field.id}Type`,
              '===',
              'UPLOAD',
            ],
          ],
          getChoices: (props) => {
            const typeKey = `pageContentFields.${field.id}Type`;
            const type = props.form.getFieldValue(typeKey);
            const choicesKey = (
                type === 'PRODUCTS'
              ? `${field.id}ChoicesProducts`
              : type === 'UPLOAD'
              ? `${field.id}Upload`
              : `${field.id}ChoicesGenerated`
            );
            let imageUrls = getKey(props.data, `pageContentFields.${
              choicesKey
            }`, []);
            if (imageUrls && !Array.isArray(imageUrls)) {
              imageUrls = [imageUrls?.src];
            }
            if (!imageUrls?.length) {
              return [];
            }
            return imageUrls.map((link) => ({
              value: link,
              // label: `Choice ${i}`,
            }));
          },
          // eslint-disable-next-line arrow-body-style
          renderChoiceCustomAfter: (choice) => {
            return (
              <PageContentFieldsImage
                src={choice.value}
                poster={choice.value}
                alt="Media Choice"
                playsInline
                autoPlay
                muted
                loop
              />
            );
          },
        }]);
        agr.push(['FieldUploadRefsHorizontal', {
          name: `pageContentFields.${field.id}Upload`,
          label: null,
          accept: [
            'image/*',
            'video/*',
          ].join(','),
          listType: 'picture',
          uploadLabel: 'Upload',
          maxCount: 1,
          skip: [
            ...skip,
            [
              'condition.testFieldValue',
              `pageContentFields.${field.id}Type`,
              '!==',
              'UPLOAD',
            ],
          ],
        }]);
      } else {
        agr.push([
          (
            field.id.toLowerCase().includes('body')
            ? 'FieldRichText'
            : 'FieldRichTextInline'
          ),
          {
            name: `pageContentFields.${field.id}`,
            label: field.label,
            skip,
            i18nGetFieldName: fieldsContentI18nGetFieldName,
            i18nTranslatable: true,
          },
        ]);
      }
    });
    return agr;
  },
  [
    ['FieldReact', {
      label: null,
      name: 'pageContentFieldLoading',
      renderContent: (props) => {
        const { RenderInputComponent } = props;
        return (
          <RenderInputComponent {...props} />
        );
      },
      // hidden: [() => true],
      RenderInputComponent: (props) => {
        const { onChange } = props;
        const value = parseInt(props.value, 10) || 0;
        useEffect(
          () => {
            let timeout = null;
            const newValue = value + 1;
            if (newValue < 9) {
              timeout = setTimeout(
                () => onChange(newValue),
                1000,
              );
            }
            return () => {
              clearTimeout(timeout);
            }
          },
          [value, onChange],
        );
        // eslint-disable-next-line no-console
        console.log('debug / value:', value);
        return null;
      },
      skip: [tests.skipIfStepNotContent],
    }],
  ],
);

export const fieldLegalId = ['FieldUploadRefs', {
  name: 'legalId',
  label: 'ID',
  rules: [['validation.isRequired']],
  listType: 'text',
  accept: 'application/pdf',
  skip: [tests.skipIfStepNotLegal],
}];
export const fieldLegalTradeLicense = ['FieldUploadRefs', {
  name: 'legalTradeLicense',
  label: 'Trade License',
  listType: 'text',
  accept: 'application/pdf',
  skip: [tests.skipIfStepNotLegal],
}];
export const fieldLegalVatCertificate = ['FieldUploadRefs', {
  name: 'legalVatCertificate',
  label: 'VAT Certificate',
  listType: 'text',
  accept: 'application/pdf',
  skip: [tests.skipIfStepNotLegal],
}];
export const fieldTrnNumber = ['FieldText', {
  name: 'trnNumber',
  label: 'TRN Value',
  skip: [tests.skipIfStepNotLegal],
}];

export const fieldContractDefault = ['FieldUploadRefsHorizontal', {
  name: 'termsAndConditions',
  label: 'Terms and Conditions',
  initialValue: {
    url: termsAndConditionsSrc,
    uid: 'packmanTermsAndConditions',
    name: 'Packman® Terms and Conditions',
  },
  listType: 'picture',
  // accept: 'application/pdf',
  showUploadList: {
    showDownloadIcon: false,
    showPreviewIcon: false,
    showRemoveIcon: false,
  },
  virtual: [() => true],
  skip: [tests.skipIfStepNotContract],
  help: `
    Read the Packman® Terms and Conditions here before applying.
  `,
}];

const MessageWrapper = styled.div`
  /* font-size: 20px; */

`;

export const fieldEndMessage = ['FieldReact', {
  name: 'endMessage',
  skip: [tests.skipIfStepNotEnd],
  renderContent: (props) => {
    const duration = 1.5 * 1000;
    const animationEnd = Date.now() + duration;
    const colors = [
      '#EFA12C',
      '#EFA12C',
      '#EFA12C',
      '#E18D4D',
      '#D57D5F',
      '#C6686E',
      '#B95876',
      '#B54D7A',
      '#B54D7A',
    ];
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 0,
      colors,
    };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    // eslint-disable-next-line consistent-return
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 100 * (timeLeft / duration);
      // since particles fall down, start a bit higher than random
      confetti(Object.assign(
        {},
        defaults,
        {
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        }
      ));
      confetti(Object.assign(
        {},
        defaults,
        {
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        }
      ));
    }, 250);

    return (
      <MessageWrapper>
        <div style={{ fontSize: '20px' }}>
          Now spend some time discovering the Packman® system.
        </div>
        <br />
        <div style={{ fontSize: '16px' }}>
          Your new store can be found on this link:
          {' '}
          <a
            href={`${getServerUrl()}/${props.data.slug}`}
            target="_blank"
            rel="noreferrer"
          >
            {process.env.REACT_APP_SERVER_HOST}
              /
            {props.data.slug}
          </a>
          <span
            onClick={
              () => copyToClipboard(`${getServerUrl()}/${props.data.slug}`)
            }
            style={{
              fontSize: '14px',
              marginLeft: '10px',
              cursor: 'pointer',
            }}
          >
            🔗
          </span>
          <br />
          <br />
          You can share this link with your friends, on your socials, or
          anywhere on the internet and people will be able to browse your
          store
          <br />
          <br />
          {/* <AntdDivider style={{ display: 'block' }} />
          <br />
          Take a look at the additional services you selected already, and
          if you feel your products could use some new,
          professionally taken photos, make sure
          to check the Photo Shoot option below!
          <br />
          <br /> */}
        </div>
      </MessageWrapper>
    );
  },
}];

export const fieldsStyle = [
  getFilterWithCol(
    ['FieldRadioAdvanced', {
      ...fieldStyleMode[1],
      // label: null,
      iconMaxHeight: '50px',
      padding: '10px',
      iconAspectRatio: 1,
    }],
    24,
  ),
  getFilterWithCol(
    ['FieldRadioAdvanced', {
      ...fieldStyleRoundness[1],
      // label: null,
      iconMaxHeight: '50px',
      padding: '10px',
      iconAspectRatio: 1,
    }],
    24,
  ),
  getFilterWithCol(
    ['FieldRadioAdvanced', {
      ...fieldStyleTransitionStyle[1],
      // label: null,
      iconMaxHeight: '50px',
      padding: '10px',
      iconAspectRatio: 1,
    }],
    24,
  ),
].map(([name, config]) => ([
  name,
  {
    ...config,
    skip: [tests.skipIfStepNotStyle],
    hidden: [],
  },
]))

export const fieldStorageMessage = ['FieldReact', {
  name: 'storageMessage',
  skip: [tests.skipIfStepNotStorage],
  renderContent: (_props) => (
    <MessageWrapper>
      {[
        ['Local Storage:', [
          'Keep control with on-site storage.',
          'Ideal for quick access and personalized management.',
        ]],
        ['Warehouse Storage:', [
          'Streamline with our warehouses.',
          'Benefit from cost-effective, automated solutions for your growth.',
        ]],
        ['Why Choose Us?', [
          'Flexibility, cost-effectiveness, and efficiency.',
          'Scalability for business growth.',
          'Secure storage facilities for peace of mind',
        ]],
      ].map(([title, list]) => (
        <React.Fragment key={title}>
          <h1>
            {title}
          </h1>
          <ul>
            {list.map((item) => (<li key={item}>{item}</li>))}
          </ul>
        </React.Fragment>
      ))}
    </MessageWrapper>
  ),
}];

export const fieldStoragePicker = ['FieldRadioAdvanced', {
  name: 'storage',
  label: null,
  initialValue: 'LOCAL',
  choiceAspectRatio: 1,
  padding: '10px',
  choices: [
    {
      value: 'LOCAL',
      label: 'Local',
      icon: '/admin/assets/icons/storage-local.png',
    },
    {
      value: 'WAREHOUSE',
      label: 'Warehouse',
      icon: '/admin/assets/icons/storage-warehouse.png',
    },
  ],
  skip: [tests.skipIfStepNotStorage],
}];

export const fieldFonts = ['FieldRadioAdvanced', {
  name: 'fontId',
  label: null,
  maxColumns: 2,
  gap: '30px 0px',
  padding: '20px',
  skip: [tests.skipIfStepNotFonts],
  initialValue: Types.FONT_PAIRINGS[0],
  renderChoiceCustomAfter: (choice) => (
    <FontPreview
      fonts={choice.fonts}
      title={choice.title}
      subtitle={choice.subtitle}
    />
  ),
  choices: (
    Types.FONT_PAIRINGS
    .map(id => ({
      title: Types.FONT_PAIRINGS_MAP[id].title,
      subtitle: Types.FONT_PAIRINGS_MAP[id].subtitle,
      fonts: Types.FONT_PAIRINGS_MAP[id].fonts,
      value: id,
    }))
  ),
}];

const FIELDS = [
  fieldI18nLanguage,
  fieldLanguageRaw,
  // fieldCurrentStep,
  // STORE
  fieldStoreName,
  fieldStoreSlug,
  fieldStoreCategories,
  cloneItem(fieldAiBioShort, null, null, {
    label: 'Bio',
    rules: [['validation.isRequired']],
    help: 'Tell us about your store.', // TODO
    skip: [tests.skipIfStepNotStore],
    autoSize: { minRows: 5, maxRows: 15 },
  }),
  cloneItem(fieldLanguagesRaw, null, null, {
    label: 'Languages',
    skip: [tests.skipIfStepNotLanguages],
  }),

  asLocalStorageField(['FieldTitleToggle', {
    name: 'show_fields_store_social',
    initialValue: false,
    title: 'Social Media Links',
    subtitle: 'Setup your social media links.',
    skip: [
      tests.skipIfStepNotStore,
    ],
  }]),
  ...fieldsSocial,

  // BRAND
  ['FieldTitle', {
    title: 'Logo',
    skip: [tests.skipIfStepNotBrand],
    // eslint-disable-next-line max-len
    subtitle: 'If you already have a logo, upload it, but if you don\'t, we can generate one for you.',
  }],
  ['FieldRadio', {
    name: 'onboarding.brandLogo',
    label: null,
    block: true,
    col: 24,
    initialValue: true,
    choices: [{
      value: true,
      label: 'Upload',
    }, {
      value: false,
      label: 'Generate',
    }],
    skip: [tests.skipIfStepNotBrand],
  }],
  ...[
    fieldBrandLogo,
    fieldBrandFavicon,
  ].map((field) => ([field[0], {
    ...field[1],
    skip: [
      ...(field[1].skip || []),
      [
        'condition.testFieldValue',
        'onboarding.brandLogo',
        '===',
        false,
      ],
    ],
  }])),
  ['FieldTitle', {
    title: 'Colors',
    skip: [tests.skipIfStepNotBrand],
    // eslint-disable-next-line max-len
    subtitle: 'Brand color is required, but if your brand has more than one color, you can add them as accent colors.',
  }],
  fieldBrandColor,
  fieldBrandAccentColorPrimary,
  fieldBrandAccentColorSecondary,

  // ADDRESS
  ['FieldGoogleAddress', {
    name: 'address',
    label: 'Address',
    skip: [tests.skipIfStepNotAddress],
  }],

  // STORAGE
  fieldStoragePicker,
  fieldStorageMessage,

  // STYLE
  ...fieldsStyle,

  // FONTS
  fieldFonts,

  // CATEGORIES
  fieldCategories,

  // PRODUCTS
  ...fieldsProducts,

  // CONTENT
  ...fieldsContent,

  // HOMEPAGE
  ...fieldsHomepage,

  // END
  fieldEndMessage,

].map(([name, config = {}]) => ([
  name,
  {
    ...config,
    help: (
      config.help
        ? (
          isString(config.help)
            // eslint-disable-next-line react/no-danger
            ? <div dangerouslySetInnerHTML={{ __html: config.help }} />
            : config.help
        )
        : undefined
    ),
  },
]));

export default FIELDS;
